import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { ThemeContextProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AdminProvider } from './contexts/AdminContext';
import { useAuth } from './contexts/AuthContext';
import { AppLoadingScreen } from './components/common/AppLoadingScreen';
import { NotificationToast } from './components/common/NotificationToast';
import { LandingPage } from './pages/SimpleLandingPage';
import { Navigation } from './components/common/Navigation';
import Dashboard from './pages/Dashboard';
import ProgramDetails from './pages/ProgramDetails';
import { Airlines } from './pages/Airlines';
import { Hotels } from './pages/Hotels';
import { Cards } from './pages/Cards';
import { Deals } from './pages/Deals';
import { FlightMap } from './pages/FlightMap';
import { Settings } from './pages/Settings';
import AdminGuard from './components/common/AdminGuard';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminProgramsPage from './pages/admin/AdminProgramsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminAuditLogsPage from './pages/admin/AdminAuditLogsPage';
import RoleManagementPage from './pages/admin/RoleManagementPage';
import './App.css';
import './utils/demoNotifications'; // Make demo notifications available globally

// Main app content component (protected routes)
const AppContent: React.FC = () => {
  return (
    <Box 
      className="App" 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        background: (theme) => theme.palette.mode === 'light' 
          ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
          : 'linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 100%)',
        transition: 'background 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Navigation />
      <Box sx={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/airlines" element={<Airlines />} />
          <Route path="/hotels" element={<Hotels />} />
          <Route path="/cards" element={<Cards />} />
          <Route path="/deals" element={<Deals />} />
          <Route path="/flight-map" element={<FlightMap />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/programs/:id" element={<ProgramDetails />} />
        </Routes>
      </Box>
    </Box>
  );
};

// Admin layout wrapper with guard
const AdminLayoutWrapper: React.FC = () => {
  return (
    <AdminGuard>
      <AdminLayout />
    </AdminGuard>
  );
};

// App Router component that handles authentication state
const AppRouter: React.FC = () => {
  const { user, loading } = useAuth();
  const { setCurrentUser } = useTheme();

  // Sync user to ThemeContext when auth state changes
  useEffect(() => {
    console.log('🔄 App: Auth state changed - User:', user ? user.email : 'null', 'Loading:', loading);
    if (user) {
      console.log('👤 App: Setting current user in ThemeContext:', user.id);
      setCurrentUser({ id: user.id });
    } else {
      console.log('🚪 App: Clearing current user from ThemeContext');
      setCurrentUser(null);
    }
  }, [user, setCurrentUser, loading]);

  if (loading) {
    console.log('⏳ App: Still loading auth state...');
    return <AppLoadingScreen />;
  }

  if (!user) {
    console.log('🔐 App: No user authenticated, showing landing page');
    return <LandingPage />;
  }

  console.log('✅ App: User authenticated, showing main app');
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Routes>
        {/* Admin routes with nested layout */}
        <Route path="/admin/*" element={<AdminLayoutWrapper />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="programs" element={<AdminProgramsPage />} />
          <Route path="analytics" element={<AdminAnalyticsPage />} />
          <Route path="notifications" element={<AdminNotificationsPage />} />
          <Route path="audit-logs" element={<AdminAuditLogsPage />} />
          <Route path="roles" element={<RoleManagementPage />} />
          <Route path="role-management" element={<RoleManagementPage />} />
        </Route>

        {/* User routes */}
        <Route path="/*" element={
          <>
            <AppContent />
            <NotificationToast />
          </>
        } />
      </Routes>
    </Box>
  );
};

function App() {
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <AdminProvider>
          <NotificationProvider>
            <Router>
              <AppRouter />
            </Router>
          </NotificationProvider>
        </AdminProvider>
      </AuthProvider>
    </ThemeContextProvider>
  );
}

export default App;
